import { AnimatePresence, motion } from 'framer-motion';
import {
  Award,
  Briefcase,
  Camera,
  CheckCircle,
  Download,
  Edit,
  FileText,
  GraduationCap,
  LogOut,
  Plus,
  Save,
  Settings,
  Star,
  Trash2,
  Upload,
  User,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ResumeParsingProgress from './ResumeParsingProgress';

const Profile = () => {
  const { isAuthenticated, user, loadUser, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('personal');
  const [editing, setEditing] = useState({
    personal: false,
    experience: false,
    education: false,
    projects: false,
    skills: false,
    preferences: false
  });
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    personal: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      location: '',
      dateOfBirth: '',
      bio: '',
      profilePicture: '',
      workAuthorization: '',
      workAuthorizationDetails: '',
      website: '',
      linkedin: '',
      github: '',
      resume: null
    },
    experience: [],
    education: [],
    projects: [],
    skills: [],
    certifications: [],
    preferences: {
      jobTypes: [],
      workModes: [],
      salaryRange: { min: 0, max: 0 },
      preferredLocations: [],
      industries: []
    }
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);

  const handleLogout = () => {
    logout();
  };

  const handleResumeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF, DOC, or DOCX file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingResume(true);
    
    try {
      const formData = new FormData();
      formData.append('resume', file);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/upload/resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        console.log('🔥 FRONTEND - Resume upload response:', data);
        console.log('🔥 FRONTEND - Job ID received:', data.data?.jobId);
        
        // Show progress tracking modal
        if (data.data.jobId) {
          console.log('🔥 FRONTEND - Showing progress modal for job:', data.data.jobId);
          setCurrentJobId(data.data.jobId);
          setShowProgress(true);
        } else {
          console.log('🔥 FRONTEND - No job ID, showing success toast');
          toast.success('Resume uploaded successfully!');
        }
        
        // Update the user data to reflect the new resume
        if (loadUser) {
          console.log('🔥 FRONTEND - Reloading user data');
          loadUser({ logoutOnFail: false });
        }
      } else {
        console.log('🔥 FRONTEND - Upload failed:', data);
        throw new Error(data.message || 'Failed to upload resume');
      }
    } catch (error) {
      console.error('Resume upload error:', error);
      toast.error(error.message || 'Failed to upload resume');
    } finally {
      setUploadingResume(false);
    }
  };

  const handleProgressComplete = (jobData) => {
    console.log('Resume parsing completed:', jobData);
    toast.success('Resume parsed successfully with AI!');
    setShowProgress(false);
    setCurrentJobId(null);
    
    // Reload user data to get updated resume with parsed data
    if (loadUser) {
      loadUser({ logoutOnFail: false });
    }
  };

  const handleProgressError = (jobData) => {
    console.log('Resume parsing failed:', jobData);
    toast.error('Resume uploaded but AI parsing failed. You can try again later.');
    setShowProgress(false);
    setCurrentJobId(null);
  };

  const handleCloseProgress = () => {
    setShowProgress(false);
    setCurrentJobId(null);
  };

  useEffect(() => {
    if (isAuthenticated && user && (!user.skills || user.skills.length === 0) && !user.jobPreferences) {
      console.log('🔥 PROFILE - Detected minimal user object, requesting full profile');
      loadUser({ logoutOnFail: false });
    }

    console.log('🔥 FRONTEND DEBUG - useEffect (auth check) triggered');
    console.log('🔥 isAuthenticated:', isAuthenticated);
    console.log('🔥 user:', user ? 'EXISTS' : 'NULL');
    console.log('🔥 FRONTEND DEBUG - User work authorization:', user?.workAuthorization);
    console.log('🔥 FRONTEND DEBUG - User work authorization details:', user?.workAuthorizationDetails);
    
    if (!isAuthenticated) {
      console.log('❌ FRONTEND - User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }

    if (user) {
      console.log('🔥 FRONTEND - User data available, updating form data');
      console.log('🔥 User data:', JSON.stringify(user, null, 2));
      console.log('🔥 User resume field specifically:', JSON.stringify(user.resume, null, 2));
      console.log('🔥 FRONTEND DEBUG - User work authorization from server:', user.workAuthorization);
      console.log('🔥 FRONTEND DEBUG - User work authorization details from server:', user.workAuthorizationDetails);
      
      const newFormData = {
        personal: {
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phone: user.phone || '',
          location: user.location || (user.address ? `${user.address.city || ''}${user.address.state ? ', ' + user.address.state : ''}${user.address.country ? ', ' + user.address.country : ''}` : ''),
          dateOfBirth: user.dateOfBirth ? user.dateOfBirth.split('T')[0] : '',
          bio: user.bio || '',
          profilePicture: user.profilePicture?.url || user.profilePicture || '',
          workAuthorization: user.workAuthorization || '',
          workAuthorizationDetails: user.workAuthorizationDetails || '',
          website: user.socialLinks?.portfolio || user.website || '',
          linkedin: user.socialLinks?.linkedin || user.linkedin || '',
          github: user.socialLinks?.github || user.github || '',
          resume: user.resume || null
        },
        experience: (user.workExperience || user.experience || []).map(exp => ({
          company: exp.company || '',
          position: exp.jobTitle || exp.position || '',
          startDate: exp.startDate ? exp.startDate.split('T')[0] : '',
          endDate: exp.endDate ? exp.endDate.split('T')[0] : '',
          current: exp.currentJob || exp.current || false,
          description: exp.description || '',
          location: exp.location || ''
        })),
        education: user.education || [],
        projects: user.projects || [],
        skills: user.skills || [],
        certifications: user.certifications || [],
        preferences: {
          jobTypes: user.jobPreferences?.preferredJobTypes || [],
          workModes: user.jobPreferences?.remoteWork ? ['Remote'] : [],
          salaryRange: user.expectedSalary || user.preferences?.salaryRange || { min: 0, max: 0 },
          preferredLocations: user.jobPreferences?.preferredLocations || [],
          industries: user.preferences?.industries || []
        }
      };
      
      console.log('🔥 FRONTEND - New form data structure:', JSON.stringify(newFormData, null, 2));
      console.log('🔥 FRONTEND - Resume data in new form data:', JSON.stringify(newFormData.personal.resume, null, 2));
      console.log('🔥 FRONTEND DEBUG - Work authorization in new form data:', newFormData.personal.workAuthorization);
      console.log('🔥 FRONTEND DEBUG - Work authorization details in new form data:', newFormData.personal.workAuthorizationDetails);
      
      setFormData(prev => {
        const workAuthChanged = prev.personal.workAuthorization !== newFormData.personal.workAuthorization;
        const workAuthDetailsChanged = prev.personal.workAuthorizationDetails !== newFormData.personal.workAuthorizationDetails;
        
        if (workAuthChanged || workAuthDetailsChanged) {
          console.log('🔥 FRONTEND DEBUG - Work authorization changed, updating form data');
          return newFormData;
        }
        
        return Object.keys(prev.personal).every(key => prev.personal[key] === '') ? newFormData : prev;
      });
    }
  }, [isAuthenticated, user, navigate]);

  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleArrayChange = (section, index, field, value) => {
    setFormData(prev => ({
        ...prev,
      [section]: prev[section].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addExperience = () => {
    setFormData(prev => ({
        ...prev,
      experience: [...prev.experience, {
        company: '',
        position: '',
        startDate: '',
        endDate: '',
        current: false,
        description: '',
        location: ''
      }]
    }));
  };

  const removeExperience = (index) => {
    setFormData(prev => ({
        ...prev,
      experience: prev.experience.filter((_, i) => i !== index)
    }));
  };

  const addEducation = () => {
    setFormData(prev => ({
        ...prev,
      education: [...prev.education, {
        institution: '',
        degree: '',
        fieldOfStudy: '',
        startDate: '',
        endDate: '',
        gpa: '',
        description: ''
      }]
    }));
  };

  const removeEducation = (index) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  const addProject = () => {
          setFormData(prev => ({
            ...prev,
      projects: [...prev.projects, {
        name: '',
        description: '',
        technologies: [],
        startDate: '',
        endDate: '',
        url: '',
        github: ''
      }]
    }));
  };

  const removeProject = (index) => {
    setFormData(prev => ({
          ...prev,
      projects: prev.projects.filter((_, i) => i !== index)
    }));
  };

  const addSkill = (skill) => {
    if (!formData.skills.includes(skill)) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, skill]
      }));
    }
  };

  const removeSkill = (skill) => {
        setFormData(prev => ({
          ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const toggleEdit = (section) => {
    setEditing(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const saveSection = async (section) => {
    try {
      setLoading(true);
      
      // API call to save section data
      const payload = {
        [section]: formData[section]
      };
      
      console.log(`Saving ${section}:`, payload);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} updated successfully!`);
      toggleEdit(section);
    } catch (error) {
      console.error(`Error saving ${section}:`, error);
      toast.error(`Failed to update ${section}`);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'experience', label: 'Experience', icon: Briefcase },
    { id: 'education', label: 'Education', icon: GraduationCap },
    { id: 'skills', label: 'Skills', icon: Star },
    { id: 'preferences', label: 'Preferences', icon: Settings }
  ];

  const ProfileSection = ({ title, children, sectionKey, editAction }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="card-luxury p-8"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-heading font-bold text-slate-900">{title}</h2>
        <button
          onClick={() => editAction ? editAction() : toggleEdit(sectionKey)}
          className={`btn-outline text-sm ${editing[sectionKey] ? 'bg-blue-50 border-blue-300 text-blue-700' : ''}`}
        >
          {editing[sectionKey] ? (
            <>
              <X className="w-4 h-4 mr-1" />
              Cancel
            </>
          ) : (
            <>
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </>
          )}
        </button>
      </div>
      {children}
      {editing[sectionKey] && (
        <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-slate-200">
          <button
            onClick={() => toggleEdit(sectionKey)}
            className="btn-outline"
          >
            Cancel
          </button>
          <button
            onClick={() => saveSection(sectionKey)}
          disabled={loading}
            className="btn-primary"
        >
            {loading ? (
            <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
            </>
          ) : (
            <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
            </>
          )}
        </button>
      </div>
      )}
    </motion.div>
  );

  const renderPersonalInfo = () => (
    <ProfileSection title="Personal Information" sectionKey="personal">
      <div className="space-y-6">
      {/* Profile Picture */}
        <div className="flex items-center space-x-6">
        <div className="relative">
          <img
            src={formData.personal.profilePicture || `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=120&h=120&fit=crop&crop=face`}
            alt="Profile"
              className="w-24 h-24 rounded-full object-cover shadow-soft"
          />
          {editing.personal && (
              <button className="absolute bottom-0 right-0 p-2 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 transition-colors">
                <Camera className="w-4 h-4" />
              </button>
          )}
        </div>
        <div>
            <h3 className="text-xl font-heading font-semibold text-slate-900">
            {formData.personal.firstName} {formData.personal.lastName}
          </h3>
            <p className="text-slate-600">{formData.personal.email}</p>
            {formData.personal.bio && (
              <p className="text-slate-500 text-sm mt-1">{formData.personal.bio}</p>
          )}
        </div>
      </div>

        {editing.personal ? (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="form-group">
              <label className="form-label">First Name</label>
          <input
            type="text"
            value={formData.personal.firstName}
                onChange={(e) => handleChange('personal', 'firstName', e.target.value)}
                className="input-luxury"
          />
        </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
          <input
            type="text"
            value={formData.personal.lastName}
                onChange={(e) => handleChange('personal', 'lastName', e.target.value)}
                className="input-luxury"
          />
        </div>
            <div className="form-group">
              <label className="form-label">Email</label>
          <input
            type="email"
            value={formData.personal.email}
                onChange={(e) => handleChange('personal', 'email', e.target.value)}
                className="input-luxury"
          />
        </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
          <input
            type="tel"
            value={formData.personal.phone}
                onChange={(e) => handleChange('personal', 'phone', e.target.value)}
                className="input-luxury"
          />
        </div>
            <div className="form-group">
              <label className="form-label">Location</label>
          <input
            type="text"
            value={formData.personal.location}
                onChange={(e) => handleChange('personal', 'location', e.target.value)}
                className="input-luxury"
                placeholder="City, State, Country"
          />
        </div>
            <div className="form-group">
              <label className="form-label">Date of Birth</label>
          <input
            type="date"
            value={formData.personal.dateOfBirth}
                onChange={(e) => handleChange('personal', 'dateOfBirth', e.target.value)}
                className="input-luxury"
          />
        </div>
            <div className="form-group md:col-span-2">
              <label className="form-label">Bio</label>
        <textarea
          value={formData.personal.bio}
                onChange={(e) => handleChange('personal', 'bio', e.target.value)}
                className="input-luxury resize-none"
                rows={3}
                placeholder="Tell us about yourself..."
        />
      </div>
            <div className="form-group">
              <label className="form-label">Website</label>
          <input
            type="url"
            value={formData.personal.website}
                onChange={(e) => handleChange('personal', 'website', e.target.value)}
                className="input-luxury"
                placeholder="https://yourwebsite.com"
          />
        </div>
            <div className="form-group">
              <label className="form-label">LinkedIn</label>
          <input
            type="url"
            value={formData.personal.linkedin}
                onChange={(e) => handleChange('personal', 'linkedin', e.target.value)}
                className="input-luxury"
            placeholder="https://linkedin.com/in/yourprofile"
          />
        </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
        <div>
                <label className="text-sm font-medium text-slate-600">Phone</label>
                <p className="text-slate-900 font-medium">{formData.personal.phone || 'Not specified'}</p>
        </div>
              <div>
                <label className="text-sm font-medium text-slate-600">Location</label>
                <p className="text-slate-900 font-medium">{formData.personal.location || 'Not specified'}</p>
      </div>
              <div>
                <label className="text-sm font-medium text-slate-600">Date of Birth</label>
                <p className="text-slate-900 font-medium">{formData.personal.dateOfBirth || 'Not specified'}</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-600">Website</label>
                <p className="text-slate-900 font-medium">
                  {formData.personal.website ? (
                    <a href={formData.personal.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                      {formData.personal.website}
                    </a>
                  ) : 'Not specified'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600">LinkedIn</label>
                <p className="text-slate-900 font-medium">
                  {formData.personal.linkedin ? (
                    <a href={formData.personal.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                      View Profile
                    </a>
                  ) : 'Not specified'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </ProfileSection>
  );

  const renderExperience = () => (
    <ProfileSection title="Work Experience" sectionKey="experience">
      <div className="space-y-6">
        {formData.experience.length === 0 ? (
          <div className="text-center py-8">
            <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">No work experience added yet</p>
            {editing.experience && (
              <button onClick={addExperience} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Add Experience
        </button>
            )}
      </div>
        ) : (
          <>
        {formData.experience.map((exp, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-6">
                {editing.experience ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <h4 className="font-heading font-semibold text-slate-900">Experience {index + 1}</h4>
              <button
                        onClick={() => removeExperience(index)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
              </button>
            </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="form-group">
                        <label className="form-label">Company</label>
                <input
                  type="text"
                  value={exp.company}
                          onChange={(e) => handleArrayChange('experience', index, 'company', e.target.value)}
                          className="input-luxury"
                />
              </div>
                      <div className="form-group">
                        <label className="form-label">Position</label>
                <input
                  type="text"
                  value={exp.position}
                          onChange={(e) => handleArrayChange('experience', index, 'position', e.target.value)}
                          className="input-luxury"
                />
              </div>
                      <div className="form-group">
                        <label className="form-label">Start Date</label>
                <input
                  type="date"
                  value={exp.startDate}
                          onChange={(e) => handleArrayChange('experience', index, 'startDate', e.target.value)}
                          className="input-luxury"
                />
              </div>
                      <div className="form-group">
                        <label className="form-label">End Date</label>
                  <input
                    type="date"
                    value={exp.endDate}
                          onChange={(e) => handleArrayChange('experience', index, 'endDate', e.target.value)}
                          className="input-luxury"
                    disabled={exp.current}
                  />
                      </div>
                      <div className="form-group md:col-span-2">
                        <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={exp.current}
                            onChange={(e) => handleArrayChange('experience', index, 'current', e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                          <span className="text-sm font-medium text-slate-700">I currently work here</span>
                  </label>
                </div>
                      <div className="form-group md:col-span-2">
                        <label className="form-label">Description</label>
                <textarea
                  value={exp.description}
                          onChange={(e) => handleArrayChange('experience', index, 'description', e.target.value)}
                          className="input-luxury resize-none"
                          rows={3}
                          placeholder="Describe your role and achievements..."
                />
              </div>
            </div>
          </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-heading font-semibold text-slate-900">{exp.position}</h4>
                        <p className="text-blue-600 font-medium">{exp.company}</p>
          </div>
                      <span className="text-sm text-slate-500">
                        {exp.startDate} - {exp.current ? 'Present' : exp.endDate || 'Not specified'}
                      </span>
                    </div>
                    {exp.description && (
                      <p className="text-slate-700 text-sm leading-relaxed">{exp.description}</p>
        )}
      </div>
                )}
              </div>
            ))}
      {editing.experience && (
              <button onClick={addExperience} className="btn-outline w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Another Experience
          </button>
            )}
          </>
        )}
      </div>
    </ProfileSection>
  );

  const renderSkills = () => (
    <ProfileSection title="Skills & Expertise" sectionKey="skills">
      <div className="space-y-6">
        {editing.skills ? (
          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Add Skills</label>
              <input
                type="text"
                placeholder="Type a skill and press Enter"
                className="input-luxury"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    addSkill(e.target.value.trim());
                    e.target.value = '';
                  }
                }}
              />
              <p className="text-sm text-slate-500 mt-1">Press Enter to add each skill</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.skills.map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                >
                  {typeof skill === 'object' && skill.name ? skill.name : skill}
        <button
                    onClick={() => removeSkill(skill)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    <X className="w-3 h-3" />
        </button>
                </span>
              ))}
      </div>
              </div>
        ) : (
          <div>
            {formData.skills.length === 0 ? (
              <div className="text-center py-8">
                <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No skills added yet</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                {formData.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 rounded-xl font-medium border border-blue-200"
                  >
                    {typeof skill === 'object' && skill.name ? skill.name : skill}
                  </span>
                ))}
            </div>
            )}
              </div>
        )}
              </div>
    </ProfileSection>
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="nav-luxury sticky top-0 z-50">
        <div className="container-luxury">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-3 group">
                <div className="w-10 h-10 gradient-bg-luxury rounded-2xl flex items-center justify-center shadow-luxury group-hover:scale-105 transition-transform">
                  <Briefcase className="w-6 h-6 text-white" />
              </div>
              <div>
                  <h1 className="text-xl font-display font-bold gradient-text">JobZentro</h1>
                  <p className="text-xs text-slate-500 font-medium">An AI HRMS Platform</p>
                </div>
              </Link>
              <nav className="hidden lg:flex space-x-8 ml-8">
                <Link to="/dashboard" className="nav-link">Dashboard</Link>
                <Link to="/jobs" className="nav-link">Browse Jobs</Link>
                <Link to="/applications" className="nav-link">Applications</Link>
                <Link to="/profile" className="nav-link active">Profile</Link>
                <Link to="/about" className="nav-link">About</Link>
                <Link to="/contact" className="nav-link">Contact</Link>
              </nav>
              </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3 p-2 rounded-xl">
                <img
                  src={user?.profilePicture || `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face`}
                  alt="Profile"
                  className="w-10 h-10 rounded-full object-cover shadow-soft"
                />
                <div className="hidden md:block text-left">
                  <p className="text-sm font-heading font-semibold text-slate-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-slate-500">Job Seeker</p>
                </div>
              </div>
          <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors p-2 rounded-xl hover:bg-slate-100"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline">Logout</span>
          </button>
        </div>
          </div>
        </div>
      </header>

      <div className="container-luxury py-8">
        {/* Page Header */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="text-center mb-8">
            <h1 className="text-4xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Your
              <span className="block gradient-text">Profile</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Manage your personal information, experience, and preferences to attract the right opportunities.
            </p>
      </div>

          <div className="flex items-center justify-center space-x-8 text-sm text-slate-600">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Profile verified</span>
              </div>
            <div className="flex items-center space-x-2">
              <Star className="w-4 h-4 text-amber-400" />
              <span>Premium member</span>
            </div>
            <div className="flex items-center space-x-2">
              <Award className="w-4 h-4 text-purple-500" />
              <span>Profile complete</span>
              </div>
              </div>
    </motion.div>

        {/* Profile Tabs */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="card-luxury p-2 mb-8 bg-white/80 backdrop-blur-sm"
        >
          <div className="flex space-x-2 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
        <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 rounded-xl font-heading font-medium transition-all duration-300 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
        </button>
              );
            })}
      </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            {activeTab === 'personal' && renderPersonalInfo()}
            {activeTab === 'experience' && renderExperience()}
            {activeTab === 'skills' && renderSkills()}
            
            {activeTab === 'education' && (
              <ProfileSection title="Education" sectionKey="education">
                <div className="text-center py-8">
                  <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Education section coming soon</p>
          </div>
              </ProfileSection>
            )}
            
            {activeTab === 'preferences' && (
              <ProfileSection title="Job Preferences" sectionKey="preferences">
                <div className="text-center py-8">
                  <Settings className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Preferences section coming soon</p>
        </div>
              </ProfileSection>
      )}
    </motion.div>
        </AnimatePresence>

        {/* Resume Section */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="card-luxury p-8 mt-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-heading font-bold text-slate-900 flex items-center">
              <FileText className="w-6 h-6 text-blue-500 mr-3" />
              Resume
            </h2>
            <div className="flex items-center space-x-3">
                <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleResumeUpload}
                className="hidden"
                id="resume-upload"
                disabled={uploadingResume}
              />
              <label 
                htmlFor="resume-upload" 
                className={`btn-outline cursor-pointer ${uploadingResume ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {uploadingResume ? (
                  <>
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {(user?.resume && (user.resume.originalName || user.resume.filename)) ? 'Replace Resume' : 'Upload Resume'}
                  </>
                )}
              </label>
          </div>
        </div>

          {(user?.resume && (user.resume.originalName || user.resume.filename)) ? (
            <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-2xl">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
            </div>
              <div className="flex-1">
                <h4 className="font-heading font-semibold text-slate-900">
                  {user.resume.originalName || user.resume.filename || 'Resume.pdf'}
                </h4>
                <p className="text-sm text-slate-600">
                  Last updated: {user.resume.updatedAt ? new Date(user.resume.updatedAt).toLocaleDateString() : 'Recently'}
                </p>
            </div>
              <div className="flex items-center space-x-2">
              <button
                  className="btn-outline text-sm"
                  onClick={() => {
                    if (user.resume?.filename) {
                      const downloadUrl = `${process.env.REACT_APP_API_URL}/api/upload/files/${user.resume.filename}`;
                      window.open(downloadUrl, '_blank');
                    }
                  }}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
              </button>
                <button 
                  className="text-red-500 hover:text-red-700 transition-colors p-2 rounded-lg hover:bg-red-50"
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to delete your resume?')) {
                      try {
                        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/upload/resume`, {
                          method: 'DELETE',
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                          }
                        });

                        if (response.ok) {
                          toast.success('Resume deleted successfully!');
                          if (loadUser) {
                            loadUser({ logoutOnFail: false });
                          }
                        } else {
                          throw new Error('Failed to delete resume');
                        }
                      } catch (error) {
                        console.error('Resume deletion error:', error);
                        toast.error('Failed to delete resume');
                      }
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                    </button>
          </div>
          </div>
          ) : (
            <div className="text-center py-12 border-2 border-dashed border-slate-300 rounded-2xl">
              <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">Upload Your Resume</h3>
              <p className="text-slate-600 mb-4">Share your resume to increase your chances of getting hired</p>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleResumeUpload}
                className="hidden"
                id="resume-upload-empty"
                disabled={uploadingResume}
              />
              <label 
                htmlFor="resume-upload-empty" 
                className={`btn-primary cursor-pointer ${uploadingResume ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {uploadingResume ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Choose File
                  </>
                )}
              </label>
              <p className="text-xs text-slate-500 mt-3">
                Supported formats: PDF, DOC, DOCX (Max 5MB)
              </p>
        </div>
          )}
        </motion.div>
      </div>

      {/* Resume Parsing Progress Modal */}
      {showProgress && currentJobId && (
        <ResumeParsingProgress
          jobId={currentJobId}
          onComplete={handleProgressComplete}
          onError={handleProgressError}
          onClose={handleCloseProgress}
        />
      )}
    </div>
  );
};

export default Profile; 